import { HHStack } from '@hinge-health/react-component-library';
import {
  FAILED_STATUSES,
  FileProcessingStatus,
  IN_FLIGHT_STATUSES,
} from '../types';
import { FilterChip } from './filter-chip';

export interface StatusFilterOption {
  value: string;
  label: string;
  /** `null` means "no status filter". */
  statuses: FileProcessingStatus[] | null;
}

/**
 * The status groupings an operator can pick from, per the v4 design spec.
 * PDGS filters by one status server-side, so a single-status option is
 * pushed to the server while a group is applied to the returned page; the
 * page owns that split, this component only reports the chosen set.
 */
export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'ALL', label: 'All', statuses: null },
  { value: 'IN_FLIGHT', label: 'In flight', statuses: IN_FLIGHT_STATUSES },
  {
    value: 'COMPLETED',
    label: 'Completed',
    statuses: [FileProcessingStatus.Completed],
  },
  {
    value: 'PARTIAL',
    label: 'Partial',
    statuses: [FileProcessingStatus.CompletedWithErrors],
  },
  { value: 'REJECTED', label: 'Rejected', statuses: FAILED_STATUSES },
];

export const STATUS_FILTER_CHIPS_TEST_ID = 'pdg-status-filter-chips';
export const statusFilterChipTestId = (value: string): string =>
  `pdg-status-filter-chip-${value}`;

const isSameStatusSet = (
  a: FileProcessingStatus[] | null,
  b: FileProcessingStatus[] | null,
): boolean => {
  if (a === null || b === null) {
    return a === b;
  }

  return a.length === b.length && a.every(status => b.includes(status));
};

interface StatusFilterChipsProps {
  statuses: FileProcessingStatus[] | null;
  onChange: (statuses: FileProcessingStatus[] | null) => void;
}

export const StatusFilterChips = ({
  statuses,
  onChange,
}: StatusFilterChipsProps): JSX.Element => (
  <HHStack
    direction="row"
    gap={1}
    flexWrap="wrap"
    role="group"
    aria-label="Filter by status"
    data-testid={STATUS_FILTER_CHIPS_TEST_ID}
  >
    {STATUS_FILTER_OPTIONS.map(option => {
      const isPressed = isSameStatusSet(option.statuses, statuses);

      return (
        <FilterChip
          key={option.value}
          label={option.label}
          isPressed={isPressed}
          onClick={(): void => onChange(option.statuses)}
          testId={statusFilterChipTestId(option.value)}
        />
      );
    })}
  </HHStack>
);
